import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  CreditCard, 
  FileText, 
  Heart, 
  Activity,
  Send,
  Hand,
  Code,
  Check
} from 'lucide-react';
import GradientButton from './ui/GradientButton';
import { FloatingNavBar } from '../components/Navbar';

const ProfileEdit = ({ onBackToProfile, userType = 'general' }) => {
  const { user } = useAuth();

  // Detect if user is a fleet user
  const isFleetUser = userType === 'fleet' || user?.role === 'TENANT_ADMIN' || user?.role === 'TENANT_USER';

  const [formData, setFormData] = useState({
    // Fleet-specific fields (for fleet users)
    ...(isFleetUser ? {
      name: user?.name || '',
      contact_info: user?.contact_info || '',
      emergency_contact: user?.emergency_contact || '',
      address: user?.address || '',
    } : {
      // General user fields
      firstName: user?.name?.split(' ')[0] || '',
      secondName: user?.name?.split(' ')[1] || '',

      // Address Information
      addressTemporary: '',
      addressPermanent: '',
      sameAsAbove: false,

      // Contact Details
      mobileNumber: user?.phone || '',
      homeNumber: '',
      emergencyNumber: '',

      // Identity Cards
      aadharCard: '',
      maAmrutamId: '',

      // Birth Details
      dateOfBirth: '',
      age: '',

      // Other Details
      weight: '',
      height: '',
      medicalPolicyId: '',
      sugar: '',
      bloodPressure: '',
      pastMedicalReports: '',
      bloodPressureSecond: '', // Second blood pressure reading
      mandatoryMedicines: '',
      additionalInformation: ''
    })
  });

  const [isRequestSent, setIsRequestSent] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckboxChange = (field, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked,
      // If "Same as above" is checked, copy temporary address to permanent
      ...(field === 'sameAsAbove' && checked ? { addressPermanent: prev.addressTemporary } : {})
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (isFleetUser) {
        // Use fleet update profile API for fleet users
        const updateData = {
          name: formData.name,
          contact_info: formData.contact_info,
          emergency_contact: formData.emergency_contact,
          address: formData.address,
        };

        console.log('🚛 Saving fleet profile:', updateData);
        const result = await authService.fleetUpdateProfile(updateData);
        console.log('✅ Fleet profile updated successfully:', result);
        alert('Fleet profile updated successfully!');
      } else {
        // Regular user profile update logic
        console.log('Saving general user profile:', formData);
        // TODO: Implement general user profile update API
        alert('Profile updated successfully!');
      }

      // Go back to profile view
      if (onBackToProfile) {
        onBackToProfile();
      }
    } catch (error) {
      console.error('❌ Profile update failed:', error);
      alert(`Failed to update profile: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSend = () => {
    console.log('Sending profile data:', formData);
    setIsRequestSent(true);
    // Here you would implement the actual send functionality
  };

  const handleRequestSent = () => {
    console.log('Request sent action');
    // Here you would implement the request sent functionality
  };

  const handleCodeAction = () => {
    console.log('Code action triggered');
    // Here you would implement the code action functionality
  };

  return (
    <div className="min-h-screen ">
      {/* Main Content Area */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="">
          <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">
            {isFleetUser ? 'Fleet Operator Details' : 'Personal Details'}
          </h1>

          {isFleetUser ? (
            /* Fleet User Form */
            <form className="space-y-4">
              {/* Fleet Operator Name */}
              <div className="space-y-1">
                <label className="block text-base font-medium text-gray-700">Fleet Operator Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                  placeholder="Enter fleet operator name"
                />
              </div>

              {/* Primary Contact */}
              <div className="space-y-1">
                <label className="block text-base font-medium text-gray-700">Primary Contact</label>
                <input
                  type="tel"
                  value={formData.contact_info}
                  onChange={(e) => handleInputChange('contact_info', e.target.value)}
                  className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                  placeholder="Enter primary contact number"
                />
              </div>

              {/* Emergency Contact */}
              <div className="space-y-1">
                <label className="block text-base font-medium text-gray-700">Emergency Contact</label>
                <input
                  type="tel"
                  value={formData.emergency_contact}
                  onChange={(e) => handleInputChange('emergency_contact', e.target.value)}
                  className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                  placeholder="Enter emergency contact number"
                />
              </div>

              {/* Address */}
              <div className="space-y-1">
                <label className="block text-base font-medium text-gray-700">Fleet Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm resize-none"
                  placeholder="Enter fleet operator address"
                />
              </div>
            </form>
          ) : (
            /* General User Form */
            <form className="space-y-4">
            {/* First Name */}
            <div className="space-y-1">
              <label className="block text-base font-medium text-gray-700">First Name</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                placeholder="Enter first name"
              />
            </div>

            {/* Second Name */}
            <div className="space-y-1">
              <label className="block text-base font-medium text-gray-700">Second Name</label>
              <input
                type="text"
                value={formData.secondName}
                onChange={(e) => handleInputChange('secondName', e.target.value)}
                className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                placeholder="Enter second name"
              />
            </div>

            {/* Residential Address */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-800">Residential Address</h2>
              
              <div className="space-y-1">
                <label className="block text-base font-medium text-gray-700">Address Temporary</label>
                <input
                  type="text"
                  value={formData.addressTemporary}
                  onChange={(e) => handleInputChange('addressTemporary', e.target.value)}
                  className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                  placeholder="Enter temporary address"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-base font-medium text-gray-700">Address Permanent</label>
                <input
                  type="text"
                  value={formData.addressPermanent}
                  onChange={(e) => handleInputChange('addressPermanent', e.target.value)}
                  className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                  placeholder="Enter permanent address"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="sameAsAbove"
                  checked={formData.sameAsAbove}
                  onChange={(e) => handleCheckboxChange('sameAsAbove', e.target.checked)}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="sameAsAbove" className="text-base font-medium text-gray-700">
                  Same as above
                </label>
              </div>
            </div>

            {/* Contact Details */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-800">Contact Details</h2>
              
              <div className="space-y-1">
                {/* <label className="block text-base font-medium text-gray-700">Mobile Number +91</label> */}
                <input
                  type="tel"
                  value={formData.mobileNumber}
                  onChange={(e) => handleInputChange('mobileNumber', e.target.value)}
                  className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                  placeholder="Enter mobile number +91"
                />
              </div>

              <div className="space-y-1">
                {/* <label className="block text-base font-medium text-gray-700">Home +91</label> */}
                <input
                  type="tel"
                  value={formData.homeNumber}
                  onChange={(e) => handleInputChange('homeNumber', e.target.value)}
                  className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                  placeholder="Enter home number +91"
                />
              </div>

              <div className="space-y-1">
                {/* <label className="block text-base font-medium text-gray-700">Emergency Number +91</label> */}
                <input
                  type="tel"
                  value={formData.emergencyNumber}
                  onChange={(e) => handleInputChange('emergencyNumber', e.target.value)}
                  className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                  placeholder="Enter emergency number +91"
                />
              </div>
            </div>

            {/* Identity Cards */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-800">Identity Cards</h2>
              
              <div className="space-y-1">
                {/* <label className="block text-base font-medium text-gray-700">Aadhar Card</label> */}
                <input
                  type="text"
                  value={formData.aadharCard}
                  onChange={(e) => handleInputChange('aadharCard', e.target.value)}
                  className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                  placeholder="Aadhar Card---- ----"
                />
              </div>

              <div className="space-y-1">
                <h3 className="text-base font-medium text-gray-700">Govt. Scheme Identity Cards</h3>
                {/* <label className="block text-base font-medium text-gray-700">Ma Amrutam Id</label> */}
                <input
                  type="text"
                  value={formData.maAmrutamId}
                  onChange={(e) => handleInputChange('maAmrutamId', e.target.value)}
                  className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                  placeholder="Ma Amrutam Id ----"
                />
              </div>
            </div>

            {/* Birth Details */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-800">Birth Details</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-base font-medium text-gray-700">D.O.B. DD/MM/YYYY</label>
                  <input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-base font-medium text-gray-700">Age _Years</label>
                  <input
                    type="number"
                    value={formData.age}
                    onChange={(e) => handleInputChange('age', e.target.value)}
                    className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                    placeholder="Enter age"
                  />
                </div>
              </div>
            </div>

            {/* Other Details */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-800">Other Details</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-base font-medium text-gray-700">Weight _kg</label>
                  <input
                    type="number"
                    value={formData.weight}
                    onChange={(e) => handleInputChange('weight', e.target.value)}
                    className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                    placeholder="Enter weight"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-base font-medium text-gray-700">Height _cm</label>
                  <input
                    type="number"
                    value={formData.height}
                    onChange={(e) => handleInputChange('height', e.target.value)}
                    className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                    placeholder="Enter height"
                  />
                </div>
              </div>

                  <div className="space-y-1">
                <label className="block text-base font-medium text-gray-700">Medical Policy Id</label>
                <input
                  type="text"
                  value={formData.medicalPolicyId}
                  onChange={(e) => handleInputChange('medicalPolicyId', e.target.value)}
                  className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                  placeholder="Enter medical policy ID"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-base font-medium text-gray-700">Sugar</label>
                <input
                  type="text"
                  value={formData.sugar}
                  onChange={(e) => handleInputChange('sugar', e.target.value)}
                  className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                  placeholder="Enter sugar level"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-base font-medium text-gray-700">Blood Pressure</label>
                <input
                  type="text"
                  value={formData.bloodPressure}
                  onChange={(e) => handleInputChange('bloodPressure', e.target.value)}
                  className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                  placeholder="Enter blood pressure"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-base font-medium text-gray-700">Past Medical Reports</label>
                <input
                  type="text"
                  value={formData.pastMedicalReports}
                  onChange={(e) => handleInputChange('pastMedicalReports', e.target.value)}
                  className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                  placeholder="Enter past medical reports"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-base font-medium text-gray-700">Blood Pressure</label>
                <input
                  type="text"
                  value={formData.bloodPressureSecond}
                  onChange={(e) => handleInputChange('bloodPressureSecond', e.target.value)}
                  className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                  placeholder="Enter second blood pressure reading"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-base font-medium text-gray-700">Mandatory Medicines</label>
                <input
                  type="text"
                  value={formData.mandatoryMedicines}
                  onChange={(e) => handleInputChange('mandatoryMedicines', e.target.value)}
                  className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none transition-all shadow-sm"
                  placeholder="Enter mandatory medicines"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-base font-medium text-gray-700">Additional Information</label>
                <textarea
                  value={formData.additionalInformation}
                  onChange={(e) => handleInputChange('additionalInformation', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-100 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all resize-none"
                  placeholder="Enter any additional information"
                />
              </div>
            </div>
            </form>
          )}

          {/* Save Changes Button */}
          <div className="mt-8 flex justify-center">
            <GradientButton
              title={isSaving ? 'Saving...' : 'Save Changes'}
              onClick={handleSave}
              disabled={isSaving}
              className={`w-full max-w-md ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
            />
          </div>
        </div>
      </div>

      {/* Floating Navigation Bar */}
      <FloatingNavBar
        tabs={['Profile', 'Notifications', 'Edit Profile']}
        activeTab="Edit Profile"
        onTabChange={(tab) => {
          console.log(`Switched to ${tab} tab`);
          // Handle tab changes here
          switch(tab) {
            case 'Profile':
              // Navigate back to profile screen
              if (onBackToProfile) {
                onBackToProfile();
              }
              break;
            case 'Notifications':
              console.log('Notifications clicked');
              break;
            default:
              break;
          }
        }}
        showLogout={false}
        className="mt-8"
      />
    </div>
  );
};

export default ProfileEdit;
